import { useHingeHealthSecurityContext } from '@hinge-health/react-component-library';
import { OktaRole } from '../../../utils/okta-roles';

/**
 * Interface defining ROI-specific route access permissions.
 * Each boolean property corresponds to a specific ROI functionality area.
 */
interface ROIRouteAccess {
  claimsIngestion: boolean;
  hingeConnectRules: boolean;
  hingeConnectRulesCreate: boolean;
  hingeConnectRulesEdit: boolean;
  hingeConnectRulesDelete: boolean;
  hingeConnectTags: boolean;
  hingeConnectAssessmentTasks: boolean;
  hingeConnectRuleAssessments: boolean;
  hingeConnectRuleAssessmentsCreate: boolean;
  hingeConnectHealthRecords: boolean;
  hingeConnectUserTags: boolean;
  partnerDataGateway: boolean;
  partnerDataGatewayAdmin: boolean; // can create/edit configurations
  partnerDataGatewayPhi: boolean; // can view raw record data (PHI)
}

/**
 * Role-to-permission mapping for ROI module access control.
 * Only defines permissions that are granted (true values).
 * All permissions default to false (most restrictive).
 *
 * To add a new role:
 * 1. Add the role to OktaRole enum in src/utils/okta-roles.ts
 * 2. Add mapping here with only the granted permissions
 * 3. Update src/utils/roles.tsx to include ROI routes for the role
 *
 * To add a new permission:
 * 1. Add boolean property to ROIRouteAccess interface above
 * 2. Add the property only to roles that should grant it
 * 3. Update components to check the new permission
 *
 * Note: PHI-related permissions (hingeConnectHealthRecords, hingeConnectUserTags,
 * partnerDataGatewayPhi) also require the user to have phiaccess: true claim.
 */
const roleRouteMapping: Record<string, Partial<ROIRouteAccess>> = {
  [OktaRole.PHI_MONITORING]: {
    claimsIngestion: true,
    hingeConnectRules: true,
    hingeConnectRulesCreate: true,
    hingeConnectRulesEdit: true,
    hingeConnectRulesDelete: true,
    hingeConnectTags: true,
    hingeConnectAssessmentTasks: true,
    hingeConnectRuleAssessments: true,
    hingeConnectRuleAssessmentsCreate: true,
    hingeConnectHealthRecords: true,
    hingeConnectUserTags: true,
    partnerDataGateway: true,
    partnerDataGatewayAdmin: true,
    partnerDataGatewayPhi: true,
  },
  [OktaRole.PHI_PRODUCT_MANAGER]: {
    claimsIngestion: true,
    hingeConnectRules: true,
    hingeConnectTags: true,
    hingeConnectAssessmentTasks: true,
    hingeConnectRuleAssessments: true,
    hingeConnectHealthRecords: true,
    hingeConnectUserTags: true,
    partnerDataGateway: true,
    partnerDataGatewayPhi: true,
  },
  [OktaRole.HEALTH_ECONOMIST]: {
    claimsIngestion: true,
    partnerDataGateway: true,
  },
  [OktaRole.OPERATIONS_MANAGER]: {
    hingeConnectRules: true,
    hingeConnectTags: true,
    hingeConnectAssessmentTasks: true,
    hingeConnectRuleAssessments: true,
    partnerDataGateway: true,
    partnerDataGatewayAdmin: true,
  },
  [OktaRole.SUPPLY_CHAIN_MANAGER]: {
    hingeConnectRules: true,
    hingeConnectTags: true,
    hingeConnectAssessmentTasks: true,
    hingeConnectRuleAssessments: true,
    partnerDataGateway: true,
  },
  [OktaRole.PRODUCTION_ASSOCIATE]: {
    hingeConnectRules: true,
    hingeConnectTags: true,
    hingeConnectAssessmentTasks: true,
    hingeConnectRuleAssessments: true,
    partnerDataGateway: true,
  },
  [OktaRole.CLIENT_SUCCESS]: {
    hingeConnectRules: true,
    hingeConnectTags: true,
    hingeConnectAssessmentTasks: true,
    hingeConnectRuleAssessments: true,
    partnerDataGateway: true,
  },
};

/**
 * Pure function to calculate route access permissions based on roles and PHI access.
 * This function is exported for testing purposes to achieve higher coverage.
 *
 * @param roles Array of role strings from auth token
 * @param phiAccess Boolean indicating PHI access permission
 * @returns ROIRouteAccess object with boolean flags for each ROI functionality area
 */
export const calculateRouteAccess = (
  roles: unknown[] | unknown,
  phiAccess: unknown,
): ROIRouteAccess => {
  const userRoles = Array.isArray(roles) ? roles : [];
  const userHasPhiAccess = Boolean(phiAccess);

  // Initialize with no access (default deny)
  const baseAccess: ROIRouteAccess = {
    claimsIngestion: false,
    hingeConnectRules: false,
    hingeConnectRulesCreate: false,
    hingeConnectRulesEdit: false,
    hingeConnectRulesDelete: false,
    hingeConnectTags: false,
    hingeConnectAssessmentTasks: false,
    hingeConnectRuleAssessments: false,
    hingeConnectRuleAssessmentsCreate: true, // Default to true - all users with rule assessments view can create
    hingeConnectHealthRecords: false,
    hingeConnectUserTags: false,
    partnerDataGateway: false,
    partnerDataGatewayAdmin: false,
    partnerDataGatewayPhi: false,
  };

  // Collect all granted permissions from user roles
  const grantedPermissions: Partial<ROIRouteAccess> = {};
  userRoles.forEach(role => {
    const roleString = String(role);
    const roleAccess = roleRouteMapping[roleString];
    if (roleAccess) {
      Object.assign(grantedPermissions, roleAccess);
    }
  });

  // Merge base (restrictive) with granted permissions
  const mergedAccess = { ...baseAccess, ...grantedPermissions };

  // Apply PHI access restriction to PHI-related permissions
  // Users need BOTH role permission AND PHI access for these features
  mergedAccess.hingeConnectHealthRecords =
    mergedAccess.hingeConnectHealthRecords && userHasPhiAccess;
  mergedAccess.hingeConnectUserTags =
    mergedAccess.hingeConnectUserTags && userHasPhiAccess;
  mergedAccess.partnerDataGatewayPhi =
    mergedAccess.partnerDataGatewayPhi && userHasPhiAccess;

  return mergedAccess;
};

/**
 * Custom hook for ROI module role-based route access control.
 *
 * This hook determines which ROI functionality areas a user can access based on their assigned roles.
 * It merges permissions from all user roles using OR logic (if any role grants access, user has access).
 *
 * @returns ROIRouteAccess object with boolean flags for each ROI functionality area
 *
 * @example
 * ```typescript
 * const routeAccess = useROIRoleBasedRoutes();
 *
 * // Check if user can access claims ingestion
 * if (routeAccess.claimsIngestion) {
 *   // Render claims ingestion components
 * }
 *
 * // Check if user can access any Hinge Connect functionality
 * const hasHingeConnectAccess = routeAccess.hingeConnectRules ||
 *                               routeAccess.hingeConnectTags ||
 *                               routeAccess.hingeConnectAssessmentTasks ||
 *                               routeAccess.hingeConnectRuleAssessments ||
 *                               routeAccess.hingeConnectHealthRecords ||
 *                               routeAccess.hingeConnectUserTags;
 * ```
 */
export const useROIRoleBasedRoutes = (): ROIRouteAccess => {
  const { hingeHealthAuthState } = useHingeHealthSecurityContext();
  const roles = hingeHealthAuthState?.accessToken?.claims?.roles;
  const phiAccessClaim = hingeHealthAuthState?.accessToken?.claims?.phiaccess;

  return calculateRouteAccess(roles, phiAccessClaim);
};
